import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFetchAccounts } from '@accountHooks';
import { useFetchCategories } from '@metadataHooks';
import { useFetchSubCategories } from '@metadataHooks';
import { useCreateTransaction } from '@/hooks/useTransactions';
import { couponDepositSchema, CouponDepositFormValues } from './couponDepositSchema';
import { buildCouponDepositDefaults, CouponDepositDefaults } from './useCouponDepositDefaults';

/** Pure helper: returns the label shown for an account in the dropdown. */
export function getAccountLabel(account: { name: string; nickname?: string | null }): string {
  return account.nickname && account.nickname.trim() !== '' ? account.nickname : account.name;
}

/** Pure helper: builds the transaction payload from form values and defaults. */
export function buildTransactionPayload(
  values: CouponDepositFormValues,
  defaults: CouponDepositDefaults,
  categoryId: string,
  subCategoryId: string,
) {
  return {
    type: 'income' as const,
    account_id: values.account_id,
    amount: values.amount,
    transaction_date: values.transaction_date,
    currency: defaults.currency,
    category_id: categoryId,
    sub_category_id: subCategoryId,
    description: defaults.description,
    notes: defaults.notes,
    fee: 0,
  };
}

interface CouponDepositBond {
  id: string;
  isin: string;
  issue_number: string;
  coupon_rate: number;
  face_value: number;
  currency: string;
}

interface CouponDepositFormProps {
  bond: CouponDepositBond;
  couponDate: string;
  couponAmount: number | null;
  onSuccess: () => void;
}

export function CouponDepositForm({ bond, couponDate, onSuccess }: CouponDepositFormProps) {
  const defaults = buildCouponDepositDefaults(bond, couponDate);

  const { data: accounts = [], isLoading: accountsLoading } = useFetchAccounts();
  const { data: categories = [] } = useFetchCategories();

  const otherIncomeCategory = categories.find(c => c.name === 'Other' && c.type === 'income');
  const { data: subCategories = [] } = useFetchSubCategories(otherIncomeCategory?.id);

  const createTransaction = useCreateTransaction();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CouponDepositFormValues>({
    resolver: zodResolver(couponDepositSchema),
    defaultValues: {
      account_id: '',
      amount: defaults.amount,
      transaction_date: defaults.transaction_date,
    },
  });

  const accountId = watch('account_id');
  const noAccounts = !accountsLoading && accounts.length === 0;

  const onSubmit = async (values: CouponDepositFormValues) => {
    if (!otherIncomeCategory) {
      toast.error('Could not find the required income category. Please ensure default categories are set up.');
      return;
    }

    const couponsSubCategory = subCategories.find(sc => sc.name === 'Coupons');
    if (!couponsSubCategory) {
      toast.error('Could not find the Coupons sub-category. Please ensure default sub-categories are set up.');
      return;
    }

    try {
      await createTransaction.mutateAsync(
        buildTransactionPayload(values, defaults, otherIncomeCategory.id, couponsSubCategory.id) as any
      );

      toast.success('Coupon payment deposited successfully.');
      onSuccess();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to record coupon deposit.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Account */}
      <div className="space-y-2">
        <Label htmlFor="account_id">Account</Label>
        {noAccounts ? (
          <p className="text-sm text-muted-foreground">No accounts available. Please create an account first.</p>
        ) : (
          <Select
            value={accountId}
            onValueChange={(val) => setValue('account_id', val, { shouldValidate: true })}
          >
            <SelectTrigger id="account_id">
              <SelectValue placeholder="Select an account" />
            </SelectTrigger>
            <SelectContent>
              {accounts.map((account) => (
                <SelectItem key={account.id} value={account.id}>
                  {getAccountLabel(account)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {errors.account_id && (
          <p className="text-sm text-destructive">{errors.account_id.message}</p>
        )}
      </div>

      {/* Amount */}
      <div className="space-y-2">
        <Label htmlFor="amount">Amount</Label>
        <Input
          id="amount"
          type="number"
          step="0.01"
          {...register('amount', { valueAsNumber: true })}
        />
        {errors.amount && (
          <p className="text-sm text-destructive">{errors.amount.message}</p>
        )}
      </div>

      {/* Transaction Date */}
      <div className="space-y-2">
        <Label htmlFor="transaction_date">Transaction Date</Label>
        <Input
          id="transaction_date"
          type="date"
          {...register('transaction_date')}
        />
        {errors.transaction_date && (
          <p className="text-sm text-destructive">{errors.transaction_date.message}</p>
        )}
      </div>

      {/* Currency (read-only) */}
      <div className="space-y-2">
        <Label htmlFor="currency">Currency</Label>
        <Input
          id="currency"
          value={defaults.currency}
          readOnly
          className="bg-muted"
        />
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={noAccounts || createTransaction.isPending}>
          {createTransaction.isPending ? 'Depositing...' : 'Deposit'}
        </Button>
      </div>
    </form>
  );
}
